import { Column, Index } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';

export enum QboJournalPostingKind {
	/** The day's own entry. */
	ORIGINAL = 'original',
	/** The difference found after the day was already posted. */
	CORRECTION = 'correction',
}

export enum QboJournalPostingStatus {
	POSTED = 'posted',
	/** QuickBooks refused or could not be reached; retried on the next run. */
	FAILED = 'failed',
	/** Not sent: something is unmapped or the entry does not balance. */
	BLOCKED = 'blocked',
	/** Nothing was invoiced or received that day. */
	NO_ACTIVITY = 'noActivity',
}

/** One debit or credit line exactly as it was (or would be) sent. */
export interface QboJournalLineModel {
	qboAccountId: string;
	accountName: string;
	description: string;
	debit: number;
	credit: number;
}

/**
 * PD-16989 — one QuickBooks summary journal entry for one day.
 *
 * Every day from the start date gets an `original` row, even when there was no
 * activity, so the posting history reads as a continuous calendar and the job
 * knows which days it has already handled.
 *
 * `amounts` is what this row contributed to the general ledger, keyed by its
 * Workd source (see qbo-journal-builder.ts) as signed numbers, debit positive.
 * Summing `amounts` over a day's posted rows gives what QuickBooks already
 * holds for that day; the difference from today's figures is what a
 * correction has to post. Keeping it by source rather than by account means a
 * remapped account does not read as a change in the day's sales.
 *
 * A failed or blocked row is rewritten by the next run rather than adding a new
 * one, so a day never collects more than one pending row per kind.
 */
@Index('qbo_journal_posting_period_uq', ['periodDate', 'kind', 'sequence'], {
	unique: true,
})
@Index('qbo_journal_posting_status_idx', ['status'])
@Entity('qbo_journal_posting', {}, { auditable: false })
export class QboJournalPostingEntity extends BaseEntity {
	/** The Workd day the amounts belong to (tenant time zone). */
	@Column('date', { name: 'periodDate' })
	public periodDate!: string;

	@Column('varchar', { name: 'kind', length: 16 })
	public kind!: QboJournalPostingKind;

	/** 0 for the original entry, 1, 2, ... for each correction to that day. */
	@Column('int', { name: 'sequence', unsigned: true, default: () => "'0'" })
	public sequence!: number;

	@Column('varchar', { name: 'status', length: 16 })
	public status!: QboJournalPostingStatus;

	/** QuickBooks JournalEntry.DocNumber, e.g. WD-2026-11-03 or WD-2026-11-03-C1. */
	@Column('varchar', { name: 'docNumber', length: 21 })
	public docNumber!: string;

	/** QuickBooks JournalEntry.TxnDate. A correction is dated the day it was found. */
	@Column('date', { name: 'txnDate' })
	public txnDate!: string;

	@Column('varchar', { name: 'qboJournalEntryId', length: 32, nullable: true })
	public qboJournalEntryId!: string | null;

	@Column('json', { name: 'amounts' })
	public amounts!: Record<string, number>;

	@Column('json', { name: 'lines' })
	public lines!: Array<QboJournalLineModel>;

	/** Net sales on the day's invoices (credit memos included). */
	@Column('decimal', {
		name: 'salesTotal',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public salesTotal!: string;

	/** Net money received (refunds included). */
	@Column('decimal', {
		name: 'paymentsTotal',
		precision: 13,
		scale: 2,
		default: () => "'0.00'",
	})
	public paymentsTotal!: string;

	@Column('int', { name: 'invoiceCount', unsigned: true, default: () => "'0'" })
	public invoiceCount!: number;

	@Column('int', { name: 'paymentCount', unsigned: true, default: () => "'0'" })
	public paymentCount!: number;

	@Column('text', { name: 'error', nullable: true })
	public error!: string | null;

	@Column('datetime', { name: 'postedDate', nullable: true })
	public postedDate!: Date | null;
}
